export const WEBINAR_STORAGE_KEYS = {
  RECORDS: 'inmeet_webinars',
  CREDENTIALS: 'inmeet_webinar_credentials',
  ACCESS_GRANTS: 'inmeet_webinar_access',
} as const

// Title rules
export const WEBINAR_TITLE_MIN_LENGTH = 2
export const WEBINAR_TITLE_MAX_LENGTH = 300
export const WEBINAR_TITLE_REGEX = /^[a-zA-Z0-9 &''\-.]+$/

// Host rules
export const WEBINAR_HOST_NAME_MIN_LENGTH = 2
export const WEBINAR_HOST_NAME_REGEX = /^[a-zA-Z ]+$/
export const WEBINAR_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Duration rules — totals computed from hrs * 60 + mins
export const WEBINAR_DURATION_MIN_MINUTES = 5
export const WEBINAR_DURATION_MAX_MINUTES = 24 * 60

// Registrants
export const WEBINAR_MAX_REGISTRANTS_MIN = 4
export const WEBINAR_MAX_REGISTRANTS_MAX = 10000

// Password
export const WEBINAR_PASSWORD_MIN_LENGTH = 6

// Domain rule for registration restriction
export const DOMAIN_REGEX = /^([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/

// Speaker rules
export const SPEAKER_NAME_MIN_LENGTH = 2
export const SPEAKER_NAME_REGEX = /^[a-zA-Z .'-]+$/

// Poll rules
export const POLL_QUESTION_MIN_LENGTH = 4
export const POLL_OPTIONS_MIN = 2
export const POLL_OPTIONS_MAX = 8

// Survey rules
export const SURVEY_QUESTION_MIN_LENGTH = 4

export const WEBINAR_ERROR_CODES = {
  WEBINAR_NOT_FOUND: 'WEBINAR_NOT_FOUND',
  WEBINAR_COMPLETED: 'WEBINAR_COMPLETED',
  WEBINAR_EXPIRED: 'WEBINAR_EXPIRED',
  WEBINAR_CANCELLED: 'WEBINAR_CANCELLED',
  PASSWORD_REQUIRED: 'PASSWORD_REQUIRED',
  INVALID_PASSWORD: 'INVALID_PASSWORD',

  TITLE_REQUIRED: 'TITLE_REQUIRED',
  TITLE_TOO_SHORT: 'TITLE_TOO_SHORT',
  TITLE_INVALID_CHARS: 'TITLE_INVALID_CHARS',
  DATE_REQUIRED: 'DATE_REQUIRED',
  DATE_PAST: 'DATE_PAST',
  TIME_REQUIRED: 'TIME_REQUIRED',
  START_IN_PAST: 'START_IN_PAST',
  DURATION_INVALID: 'DURATION_INVALID',
  DURATION_TOO_LONG: 'DURATION_TOO_LONG',
  HOST_NAME_REQUIRED: 'HOST_NAME_REQUIRED',
  HOST_NAME_INVALID: 'HOST_NAME_INVALID',
  HOST_EMAIL_REQUIRED: 'HOST_EMAIL_REQUIRED',
  HOST_EMAIL_INVALID: 'HOST_EMAIL_INVALID',
  PASSWORD_TOO_SHORT: 'PASSWORD_TOO_SHORT',
  MAX_REGISTRANTS_INVALID: 'MAX_REGISTRANTS_INVALID',

  REG_FIELDS_REQUIRED: 'REG_FIELDS_REQUIRED',
  DOMAIN_INVALID: 'DOMAIN_INVALID',
  DOMAIN_LIST_EMPTY: 'DOMAIN_LIST_EMPTY',

  REMINDER_TIME_INVALID: 'REMINDER_TIME_INVALID',
  POST_EMAIL_TIME_INVALID: 'POST_EMAIL_TIME_INVALID',

  SPEAKER_NAME_REQUIRED: 'SPEAKER_NAME_REQUIRED',
  SPEAKER_NAME_INVALID: 'SPEAKER_NAME_INVALID',
  SPEAKER_EMAIL_INVALID: 'SPEAKER_EMAIL_INVALID',

  POLL_QUESTION_REQUIRED: 'POLL_QUESTION_REQUIRED',
  POLL_NEEDS_TWO_OPTIONS: 'POLL_NEEDS_TWO_OPTIONS',

  SURVEY_TITLE_REQUIRED: 'SURVEY_TITLE_REQUIRED',
  SURVEY_QUESTION_REQUIRED: 'SURVEY_QUESTION_REQUIRED',

  UNKNOWN: 'UNKNOWN',
} as const

export type WebinarErrorCode =
  (typeof WEBINAR_ERROR_CODES)[keyof typeof WEBINAR_ERROR_CODES]

export const WEBINAR_ERROR_MESSAGES: Record<WebinarErrorCode, string> = {
  [WEBINAR_ERROR_CODES.WEBINAR_NOT_FOUND]: "We couldn't find that webinar.",
  [WEBINAR_ERROR_CODES.WEBINAR_COMPLETED]: 'This webinar has already been completed.',
  [WEBINAR_ERROR_CODES.WEBINAR_EXPIRED]: 'This webinar has ended.',
  [WEBINAR_ERROR_CODES.WEBINAR_CANCELLED]: 'This webinar was cancelled by the host.',
  [WEBINAR_ERROR_CODES.PASSWORD_REQUIRED]: 'This webinar requires a password.',
  [WEBINAR_ERROR_CODES.INVALID_PASSWORD]: 'Incorrect password. Please try again.',

  [WEBINAR_ERROR_CODES.TITLE_REQUIRED]: 'Webinar title is required.',
  [WEBINAR_ERROR_CODES.TITLE_TOO_SHORT]: 'Webinar title must be at least 2 characters.',
  [WEBINAR_ERROR_CODES.TITLE_INVALID_CHARS]:
    "Only letters, numbers, spaces and & ' - . are allowed.",
  [WEBINAR_ERROR_CODES.DATE_REQUIRED]: 'Please choose a date.',
  [WEBINAR_ERROR_CODES.DATE_PAST]: 'Date cannot be in the past.',
  [WEBINAR_ERROR_CODES.TIME_REQUIRED]: 'Please choose a start time.',
  [WEBINAR_ERROR_CODES.START_IN_PAST]: 'Start time must be in the future.',
  [WEBINAR_ERROR_CODES.DURATION_INVALID]: 'Minimum duration is 5 minutes.',
  [WEBINAR_ERROR_CODES.DURATION_TOO_LONG]: 'Webinars cannot be longer than 24 hours.',
  [WEBINAR_ERROR_CODES.HOST_NAME_REQUIRED]: 'Host name is required.',
  [WEBINAR_ERROR_CODES.HOST_NAME_INVALID]: 'Host name should contain letters only.',
  [WEBINAR_ERROR_CODES.HOST_EMAIL_REQUIRED]: 'Host email is required.',
  [WEBINAR_ERROR_CODES.HOST_EMAIL_INVALID]: 'Please enter a valid email address.',
  [WEBINAR_ERROR_CODES.PASSWORD_TOO_SHORT]: 'Password must be at least 6 characters.',
  [WEBINAR_ERROR_CODES.MAX_REGISTRANTS_INVALID]:
    'Maximum registrants must be at least 4.',

  [WEBINAR_ERROR_CODES.REG_FIELDS_REQUIRED]:
    'At least one registration field is required.',
  [WEBINAR_ERROR_CODES.DOMAIN_INVALID]: 'One or more domains are invalid.',
  [WEBINAR_ERROR_CODES.DOMAIN_LIST_EMPTY]:
    'Add at least one domain when domain restriction is enabled.',

  [WEBINAR_ERROR_CODES.REMINDER_TIME_INVALID]: 'Reminder time must be greater than 0.',
  [WEBINAR_ERROR_CODES.POST_EMAIL_TIME_INVALID]:
    'Post-webinar email time must be greater than 0.',

  [WEBINAR_ERROR_CODES.SPEAKER_NAME_REQUIRED]: 'Speaker name is required.',
  [WEBINAR_ERROR_CODES.SPEAKER_NAME_INVALID]:
    'Speaker name should contain letters only.',
  [WEBINAR_ERROR_CODES.SPEAKER_EMAIL_INVALID]:
    'Please enter a valid speaker email.',

  [WEBINAR_ERROR_CODES.POLL_QUESTION_REQUIRED]: 'Poll question is required.',
  [WEBINAR_ERROR_CODES.POLL_NEEDS_TWO_OPTIONS]: 'A poll needs at least 2 options.',

  [WEBINAR_ERROR_CODES.SURVEY_TITLE_REQUIRED]: 'Survey title is required.',
  [WEBINAR_ERROR_CODES.SURVEY_QUESTION_REQUIRED]:
    'At least one survey question is required.',

  [WEBINAR_ERROR_CODES.UNKNOWN]: 'Something went wrong. Please try again.',
}
